import type { WidgetHeaderElement } from '.';

/**
 * Common header elements shared across all widget types.
 * These define standard UI elements like channel picker, widget name, tabs, and window controls.
 *
 * Note: Uses `as const satisfies` to preserve literal types for use with makeHeaderElements.
 *
 * @deprecated Use {@link COMMON_MENU_HEADER_ELEMENTS} for widgets adopting the nameOverflowMenu pattern,
 * where the widget name serves as the overflow menu trigger.
 */
export const COMMON_HEADER_ELEMENTS = [
    // This is the dropdown where overflowing header elements are rendered
    // This is defined as a header element and not defined statically in the WidgetHeader render so that we can customize the render position
    {
        type: 'overflowDropdown',
        position: 15,
        alwaysVisible: true,
    },
    {
        type: 'channelPicker',
        position: 10,
        // Always keep channel picker visible the longest
        priority: 100,
    },
    {
        type: 'spacer',
        position: 50,
        alwaysVisible: true,
    },
    // Display widget name first in the overflow dropdown
    { type: 'widgetName', position: 20, priority: 4, overflowPosition: 1 },
    { type: 'tabs', position: 996, priority: 996, overflowPosition: 3 },
    { type: 'minimize', position: 997, priority: 997, desktopOnly: true },
    { type: 'maximize', position: 998, priority: 998 },
    { type: 'delete', position: 999, priority: 999 },
] as const satisfies readonly WidgetHeaderElement[];

/** Element types excluded from standalone pages (no workspace window management). */
const WORKSPACE_ONLY_ELEMENT_TYPES: ReadonlySet<WidgetHeaderElement['type']> = new Set([
    'tabs',
    'minimize',
    'maximize',
    'delete',
]);

export const STANDALONE_COMMON_HEADER_ELEMENTS = COMMON_HEADER_ELEMENTS.filter(
    (el) => !WORKSPACE_ONLY_ELEMENT_TYPES.has(el.type),
);

/**
 * Common header elements for widgets using the nameOverflowMenu pattern.
 * Replaces both `overflowDropdown` and `widgetName` with a unified `nameOverflowMenu`
 * where the widget name serves as the overflow menu trigger.
 *
 * The nameOverflowMenu collapses to a faBars icon when its priority is reached in the
 * overflow sequence, but is never fully removed from the header.
 *
 * Note: Uses `as const satisfies` to preserve literal types for use with makeHeaderElements.
 */
export const COMMON_MENU_HEADER_ELEMENTS = [
    {
        type: 'channelPicker',
        position: 10,
        // Always keep channel picker visible the longest
        priority: 100,
    },
    // The widget name doubles as the overflow menu trigger.
    // When its priority is reached, it collapses to a faBars icon rather than being removed.
    { type: 'nameOverflowMenu', position: 15, priority: 500 },
    {
        type: 'spacer',
        position: 50,
        alwaysVisible: true,
    },
    { type: 'tabs', position: 996, priority: 996, overflowPosition: 3 },
    { type: 'minimize', position: 997, priority: 997, desktopOnly: true },
    { type: 'maximize', position: 998, priority: 998 },
    { type: 'delete', position: 999, priority: 999 },
] as const satisfies readonly WidgetHeaderElement[];

export const STANDALONE_COMMON_MENU_HEADER_ELEMENTS = COMMON_MENU_HEADER_ELEMENTS.filter(
    (el) => !WORKSPACE_ONLY_ELEMENT_TYPES.has(el.type),
);
